import { Link, useNavigate } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";

import s from "../wizard.module.css";
import Confirmation from "./Steps/Confirmation/Confirmation";
import Filial from "./Steps/Filial/Filial";
import Propietario from "./Steps/Owner/Owner";
import Autorizados from "./Steps/Authorized/Authorized";
import { useResidents } from "../../hooks/useResidents";
import {
  validateFilial,
  validatePropietario,
  validateClienteIdentidad,
  validateClienteContacto,
} from "../../services/validationRules";
import { useEsCliente } from "../../../../shared/hooks/useTenantTipo";
import { useLang } from "../../../../shared/i18n/useLang";
import {
  crearResidenteCompleto,
  actualizarResidenteCompleto,
  obtenerResidenteCompleto,
} from "../../services/residentService";

// Construye los pasos del asistente según el tipo de organización.
//   - condominio: Filial → Propietario → Autorizados → Confirmación
//   - cliente:    Identificación → Contacto → Confirmación (ID automático)
// Cada paso trae su rótulo (el de la barra), su título y su bajada, para que
// el encabezado se dibuje siempre igual desde el armazón y no desde el paso.
function buildSteps(esCliente, esEdicion, L) {
  if (esCliente) {
    return [
      {
        key: "propietario",
        label: L("Identificación", "Identification"),
        corto: L("Identidad", "Identity"),
        titulo: esEdicion ? L("Editar cliente", "Edit client") : L("Registrar cliente", "Register client"),
        bajada: L(
          "Complete la identificación legal del cliente y su dirección.",
          "Enter the client's legal identification and address.",
        ),
        component: Propietario,
        props: { section: "identidad" },
        validate: validateClienteIdentidad,
      },
      {
        key: "propietario",
        label: L("Contacto", "Contact"),
        corto: L("Contacto", "Contact"),
        titulo: L("Datos de contacto", "Contact details"),
        bajada: L(
          "Teléfonos y correos a los que se enviarán facturas y recibos.",
          "Phones and emails where invoices and receipts will be sent.",
        ),
        component: Propietario,
        props: { section: "contacto" },
        validate: validateClienteContacto,
      },
      {
        label: L("Confirmación", "Confirmation"),
        corto: L("Confirmar", "Confirm"),
        titulo: L("Confirmación y Resumen", "Confirmation & Summary"),
        bajada: L(
          "Verifique los datos antes de completar el registro en el sistema.",
          "Check the data before completing the record in the system.",
        ),
        component: Confirmation,
      },
    ];
  }

  return [
    {
      key: "filial",
      label: L("Filial", "Unit"),
      corto: L("Filial", "Unit"),
      titulo: esEdicion ? L("Editar filial", "Edit unit") : L("Registrar filial", "Register unit"),
      bajada: L(
        "Complete los datos de identificación y registro catastral de la unidad.",
        "Enter the identification and land registry details of the unit.",
      ),
      component: Filial,
      validate: validateFilial,
    },
    {
      key: "propietario",
      label: L("Propietario", "Owner"),
      corto: L("Propietario", "Owner"),
      titulo: esEdicion ? L("Editar propietario", "Edit owner") : L("Registrar propietario", "Register owner"),
      bajada: L(
        "Complete los datos personales, de contacto y vehículos del propietario de la filial.",
        "Enter the owner's personal details, contact information and vehicles.",
      ),
      component: Propietario,
      validate: validatePropietario,
    },
    {
      label: L("Autorizados", "Authorized"),
      corto: L("Autorizados", "Authorized"),
      titulo: L("Registrar autorizados", "Register authorized people"),
      bajada: L(
        "Agregue personas autorizadas para el acceso a la filial y sus vehículos asociados. (opcional)",
        "Add people authorized to enter the unit and their vehicles. (optional)",
      ),
      component: Autorizados,
    },
    {
      label: L("Confirmación", "Confirmation"),
      corto: L("Confirmar", "Confirm"),
      titulo: esEdicion ? L("Resumen de cambios", "Summary of changes") : L("Confirmación y Resumen", "Confirmation & Summary"),
      bajada: esEdicion
        ? L("Revise lo que se va a modificar antes de guardar.", "Review what will change before saving.")
        : L(
            "Verifique los datos antes de completar el registro oficial en el sistema.",
            "Check the data before completing the official record in the system.",
          ),
      component: Confirmation,
    },
  ];
}

// Errores de validación del backend ("filial.numFolio", "propietario.vehiculos.0"...)
// -> paso del asistente donde se corrigen y errores por campo de ese paso.
function erroresDelBackend(errores) {
  const claves = Object.keys(errores || {});
  if (!claves.length) return { paso: null, campos: {} };
  const seccion = (k) => k.split(".")[0];
  const paso = claves.some((k) => seccion(k) === "filial") ? 0
    : claves.some((k) => ["propietario", "copropietarios"].includes(seccion(k))) ? 1
    : claves.some((k) => seccion(k) === "autorizados") ? 2
    : null;
  const NOMBRES = { tipo_propiedad: "tipoPropiedad", emails: "emailPrincipal" };
  const campos = {};
  claves.forEach((k) => {
    const [sec, campo, idx] = k.split(".");
    if (!((paso === 0 && sec === "filial") || (paso === 1 && sec === "propietario"))) return;
    if (campo === "vehiculos") {
      campos.vehiculos = campos.vehiculos || [];
      campos.vehiculos[Number(idx)] = errores[k];
    } else {
      campos[NOMBRES[campo] || campo] = errores[k];
    }
  });
  return { paso, campos };
}

const INITIAL_FORM = {
  filial: {
    idFilial: "",
    numPlano: "",
    numFolio: "",
    tipoPropiedad: "",
  },
  propietario: {
    cedula: "",
    tipo_cedula: "",
    nombre: "",
    apellidos: "",
    nombre_sociedad: "",
    telefono_casa: "",
    telefono_cel: "",
    telefono_trabajo: "",
    es_sociedad: false,
    vehiculos: [],
    notas: "",
    sin_correo: false,
    emailPrincipal: "",
    emailExtras: [],
    direccion_provincia: "",
    direccion_canton: "",
    direccion_distrito: "",
    direccion_senas: "",
  },
  copropietarios: [],
  autorizados: [],
};

// filialId: si viene, el asistente edita esa filial (modo condominio) con
// todos sus datos cargados, y al final muestra un resumen de los cambios.
export default function Stepper({ filialId = null }) {
  const navigate = useNavigate();
  const [isLoading, setLoading] = useState(false);
  const { createResident } = useResidents();
  const esCliente = useEsCliente();
  const L = useLang();
  const esEdicion = !!filialId;

  const STEPS = useMemo(() => buildSteps(esCliente, esEdicion, L), [esCliente, esEdicion, L]);

  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [original, setOriginal] = useState(null); // datos guardados, para el resumen de cambios
  const [cargando, setCargando] = useState(esEdicion);
  const [errorCarga, setErrorCarga] = useState("");
  const [errors, setErrors] = useState({}); // errores del paso actual

  useEffect(() => {
    if (!esEdicion) return;
    let cancelado = false;
    setCargando(true);
    obtenerResidenteCompleto(filialId)
      .then((data) => {
        if (cancelado) return;
        setFormData(data);
        setOriginal(JSON.parse(JSON.stringify(data)));
      })
      .catch((e) => !cancelado && setErrorCarga(e.message))
      .finally(() => !cancelado && setCargando(false));
    return () => { cancelado = true; };
  }, [esEdicion, filialId]);

  // ── Validar el paso actual ────────────────────────────────────────────────
  function validateStep(step) {
    const def = STEPS[step];
    if (def?.validate && def?.key) return def.validate(formData[def.key]);
    return {}; // pasos opcionales / confirmación
  }

  function irAPaso(step) {
    setErrors({});
    setActiveStep(Math.max(0, Math.min(step, STEPS.length - 1)));
  }

  function handleNext(e) {
    e.preventDefault();
    const errs = validateStep(activeStep);
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      // Llevar la vista al primer campo con problema
      setTimeout(() => {
        const el = document.querySelector("[data-has-error='true']");
        el?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 50);
      return;
    }
    setErrors({});
    setActiveStep((s) => Math.min(s + 1, STEPS.length - 1));
  }

  function handlePrev(e) {
    e.preventDefault();
    setErrors({});
    setActiveStep((s) => Math.max(s - 1, 0));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (isLoading) return;
    setErrors({});
    try {
      setLoading(true);
      if (esCliente) await createResident(formData);            // modo cliente: flujo de siempre
      else if (esEdicion) await actualizarResidenteCompleto(filialId, formData);
      else await crearResidenteCompleto(formData);
      navigate("/residentes");
    } catch (err) {
      console.error("Fallo la secuencia de creación:", err);
      const { paso, campos } = erroresDelBackend(err.errores);
      if (paso !== null) setActiveStep(paso);
      setErrors({ _global: err.message || "Error al guardar. Revisá los datos e intentá de nuevo.", ...campos });
    } finally {
      setLoading(false);
    }
  }

  const paso = STEPS[activeStep];
  const esUltimo = activeStep === STEPS.length - 1;
  const hasErrors = Object.keys(errors).length > 0;

  const textoGuardar = esEdicion
    ? L("Guardar cambios", "Save changes")
    : esCliente
      ? L("Guardar Cliente", "Save Client")
      : L("Confirmar y Guardar", "Confirm & Save");

  return (
    <div className={s.wizard}>
      {/* Migas: el mismo formato que el resto del sistema */}
      <span className={s.inactive}>
        <Link to="/">
          <span className={s.inactive}>{L("Inicio", "Home")} </span> /
        </Link>
        <Link to="/residentes">
          <span className={s.inactive}>
            {" "}
            {esCliente ? L("Clientes", "Clients") : L("Residentes", "Residents")}{" "}
          </span>
          /
        </Link>
        <span className={s.active}> {esEdicion ? L("Editar", "Edit") : L("Agregar", "Add")}</span>
      </span>

      <div className={s.shell}>
        <form
          className={esCliente ? `${s.card} ${s.cardNarrow}` : s.card}
          onSubmit={(e) => e.preventDefault()}
        >
          {cargando || errorCarga ? (
            <div className={s.cardBody}>
              <div className={errorCarga ? `${s.loading} ${s.loadingError}` : s.loading}>
                {errorCarga ? (
                  <>
                    <i className="fa-solid fa-circle-exclamation" />
                    {errorCarga}
                    <Link to="/residentes">
                      <span className={s.active}>{L("Volver", "Back")}</span>
                    </Link>
                  </>
                ) : (
                  <>
                    <i className="fa-solid fa-circle-notch fa-spin" />
                    {L("Cargando datos…", "Loading…")}
                  </>
                )}
              </div>
            </div>
          ) : (
            <>
              <div className={s.cardBody}>
                {/* ── Encabezado: píldora, título, bajada y barra de pasos ── */}
                <div className={s.header}>
                  <span className={s.stepPill}>
                    <span className={s.pillDot} />
                    {L("Paso", "Step")} {activeStep + 1} {L("de", "of")} {STEPS.length}
                  </span>
                  {esEdicion && formData.filial?.idFilial && (
                    <span className={`${s.pill} ${s.pillNeutral} ${s.pillRound} ${s.headerPill}`}>
                      <i className="fa-solid fa-house" />
                      {formData.filial.idFilial}
                    </span>
                  )}
                  <h1 className={s.title}>{paso.titulo}</h1>
                  <p className={s.subtitle}>{paso.bajada}</p>

                  <div className={s.steps}>
                    {/* Línea de fondo partida en tramos: verde lo ya recorrido */}
                    <div className={s.track}>
                      {STEPS.slice(1).map((st, i) => (
                        <div
                          key={`seg-${i}`}
                          className={i < activeStep ? `${s.segment} ${s.segmentDone}` : s.segment}
                        />
                      ))}
                    </div>

                    {STEPS.map((st, i) => (
                      <div
                        key={`${st.label}-${i}`}
                        className={[
                          s.step,
                          i === activeStep ? s.stepActive : "",
                          i < activeStep ? s.stepDone : "",
                        ].filter(Boolean).join(" ")}
                      >
                        <span className={s.circle}>
                          {i < activeStep ? <i className="fa-solid fa-check" /> : i + 1}
                        </span>
                        <span className={s.stepLabel}>
                          <span className={s.labelLong}>{st.label}</span>
                          <span className={s.labelShort}>{st.corto}</span>
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* ── Aviso de errores del paso ── */}
                {hasErrors && (
                  <div className={s.alert}>
                    <i className="fa-solid fa-triangle-exclamation" />
                    {errors._global ||
                      L(
                        "Hay campos con errores. Revisalos antes de continuar.",
                        "Some fields have errors. Review them before continuing.",
                      )}
                  </div>
                )}

                {/* ── Paso actual ── */}
                <div className={s.body}>
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={activeStep}
                      initial={{ opacity: 0, x: 24 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -24 }}
                      transition={{ duration: 0.25 }}
                    >
                      {(() => {
                        const Component = paso.component;
                        return (
                          <Component
                            formData={formData}
                            setFormData={setFormData}
                            errors={errors}
                            original={original}
                            irAPaso={irAPaso}
                            {...(paso.props || {})}
                          />
                        );
                      })()}
                    </motion.div>
                  </AnimatePresence>
                </div>
              </div>

              {/* ── Pie pegado a la base de la tarjeta ── */}
              <div className={s.footerBar}>
                <button
                  type="button"
                  className={`${s.btnGhost} ${s.footerBtn}`}
                  onClick={handlePrev}
                  disabled={activeStep === 0}
                >
                  {L("Atrás", "Back")}
                </button>

                {!esUltimo ? (
                  <button
                    type="button"
                    className={[
                      s.btnPrimary,
                      s.footerBtnMain,
                      hasErrors ? s.btnError : "",
                    ].filter(Boolean).join(" ")}
                    onClick={handleNext}
                  >
                    {hasErrors ? (
                      <>
                        <i className="fa-solid fa-circle-exclamation" />
                        {L("Corregir errores", "Fix errors")}
                      </>
                    ) : (
                      L("Siguiente", "Next")
                    )}
                  </button>
                ) : (
                  <button
                    type="submit"
                    className={`${s.btnPrimary} ${s.footerBtnMain}`}
                    disabled={isLoading}
                    onClick={handleSubmit}
                  >
                    {isLoading ? (
                      <>
                        <i className="fa-solid fa-circle-notch fa-spin" />
                        {L("Guardando…", "Saving…")}
                      </>
                    ) : (
                      textoGuardar
                    )}
                  </button>
                )}
              </div>
            </>
          )}
        </form>
      </div>
    </div>
  );
}
